<template>
  <rest-page :fetch-endpoint="fetchEndpoint">
    <page-header-new :title="pageTitle" :breadcrumbs="breadcrumbs">
      <template slot="actions">
        <save-button
          :click="saveSettings"
          data-qa="create-project-btn"
          v-if="!readonly"
        />
      </template>
    </page-header-new>
    <page-content>
      <rest-form
        :save-endpoint="saveEndpoint"
        :after-save="afterSave"
        :model="model"
        :rules="rules"
        :disabled="readonly"
        ref="form"
      >
        <template v-if="model">
          <project-settings-section
            v-model="model"
            :companies="companies"
            :providers="providers"
            :timezones="timezones"
          />
        </template>
      </rest-form>
      <rest-form
        :save-endpoint="saveEndpoint"
        :after-save="afterSave"
        :model="model"
        :rules="rules"
        :disabled="readonly"
        :success-message="$t('ProjectSettings_Message_CancelDeleteSuccess')"
        ref="cancel"
      >
        <form-section
          v-if="canDelete && projectId"
          :title="$t('ProjectSettings_Delete_Title')"
        >
          <template slot="controls">
            <el-button
              v-if="isProjectActive"
              type="danger"
              icon="el-icon-delete"
              @click="confirmDelete"
            >
              {{ $t('ProjectSettings_Button_DeleteProject') }}
            </el-button>
            <el-button
              v-if="isProjectNotActive"
              class="cancel-delete-button"
              @click="cancelDelete"
            >
              {{ $t('ProjectSettings_Button_Cancel_DeleteProject') }}
            </el-button>
          </template>
          <el-form v-if="isProjectActive">
            <form-item :label="$t('ProjectSettings_Delete_Description')">
            </form-item>
          </el-form>
          <el-form v-if="isProjectNotActive">
            <form-item :label="deletionMessage"></form-item>
          </el-form>
        </form-section>
      </rest-form>
    </page-content>
    <save-features-dialog
      :features="changedFeatures"
      @close="showDialog = false"
      v-if="showDialog"
      :save="save"
    />
  </rest-page>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import FormItem from '@/common/components/form/FormItem'
import PageHeaderNew from '@/common/components/page/PageHeaderNew'
import RestForm from '@/common/components/rest/RestForm'
import RestPage from '@/common/components/rest/RestPage'
import PageContent from '@/common/components/page/PageContent'
import SaveButton from '@/common/components/form/SaveButton'
import SaveFeaturesDialog from '@/modules/project/components/SaveFeaturesDialog'
import RestApi from '@/core/services/RestApi'
import i18n from '@/core/utils/I18n'
import moment from 'moment'
import { projectRoute, projectEndpoint } from '@/core/helpers'
import { ProjectStatusEnum } from '@/common/enum/ProjectStatusEnum'
import { mapGetters } from 'vuex'
import ProjectSettingsSection from '@/modules/project/components/ProjectSettingsSection'

export default {
  name: 'ProjectSettingsPage',

  components: {
    ProjectSettingsSection,
    SaveFeaturesDialog,
    SaveButton,
    PageContent,
    RestPage,
    RestForm,
    PageHeaderNew,
    FormItem,
    FormSection
  },

  data() {
    return {
      model: this.defaultModel(),
      companies: [],
      providers: [],
      timezones: [],
      features: {},
      showDialog: false,
      breadcrumbs: [
        { label: this.$t('Settings_Breadcrumb_Settings') },
        { label: this.$t('LeftNavigation_ProjectSettings') }
      ],
      deletionDate: moment
        .unix(this.$store.getters.deleteTime)
        .format(this.dateFormat()),
      invertedProperties: ['profile_importer_type']
    }
  },

  computed: {
    ...mapGetters(['isProjectActive', 'isProjectNotActive', 'projectId']),
    pageTitle() {
      return (
        this.$store.getters.embeddedTitle || this.$t(this.$route.meta.title)
      )
    },
    fetchEndpoint() {
      return [
        this.fetchModelEndpoint,
        this.fetchCompaniesProvidersEndpoint,
        this.fetchTimezonesEndpoint
      ]
    },
    deleteEndpoint() {
      return projectEndpoint({
        method: 'delete',
        url: ``
      })
    },
    fetchModelEndpoint() {
      if (!this.isCreatePage) {
        return {
          method: 'get',
          url: `projects/${this.$route.query.project_id}`,
          params: {
            expand: 'content_fetch'
          },
          handler: data => {
            this.model = { title: data.name, ...data }
            this.features = { ...this.model }
          }
        }
      }
      return null
    },
    fetchCompaniesProvidersEndpoint() {
      return {
        method: 'get',
        url: `companies-providers`,
        handler: data => {
          this.companies = data.companies
          this.providers = data.providers
        }
      }
    },
    fetchTimezonesEndpoint() {
      return {
        method: 'get',
        url: `timezones`,
        handler: data => {
          this.timezones = data
        }
      }
    },
    saveEndpoint() {
      if (!this.isCreatePage) {
        return {
          method: 'patch',
          url: `projects/${this.$route.query.project_id}`
        }
      }
      return {
        method: 'post',
        url: `projects`
      }
    },
    rules() {
      return {
        title: {
          required: true
        },
        provider_id: {
          required: true
        },
        company_id: {
          required: true
        },
        timezone: {
          required: true
        },
        region: {
          required: true
        }
      }
    },
    readonly() {
      return !this.$can('updateProject')
    },
    canDelete() {
      return this.$can('deleteProject')
    },
    isCreatePage() {
      return !this.$route.query.project_id
    },
    changedFeatures() {
      return Object.keys(this.features).reduce((changedFeatures, key) => {
        if (this.features[key] !== this.model[key]) {
          changedFeatures.push({
            name: key,
            status: this.model[key],
            inverted: this.invertedProperties.includes(key)
          })
        }
        return changedFeatures
      }, [])
    },
    deletionMessage() {
      return this.$t('ProjectSettings_DeletionMessage') + this.deletionDate
    }
  },

  methods: {
    dateFormat() {
      // set locale to en-GB to display the default date in dd/mm/yyyy format
      const locale = i18n.locale === 'en' ? 'en-GB' : i18n.locale
      return moment.localeData(locale).longDateFormat('L')
    },
    defaultModel() {
      if (!this.$route.query.project_id) {
        return {
          region: this.$store.state.app.region,
          subscription_tracking: 1,
          audit_log_campaign: 1,
          disable_event_triggered_campaign_timeseries_segment: 1,
          campaign_templates: 1,
          enable_new_goal_conversion: 0,
          campaign_content_preview: 1,
          preaggregated_clicked_links: 1,
          segmentation_events_use_profile: 1,
          profile_importer_type: 0
        }
      }
      return null
    },
    save() {
      this.$refs['form'].save()
      this.showDialog = false
    },
    cancelDeleteSave() {
      this.$refs['cancel'].save()
      this.showDialog = false
    },
    saveSettings() {
      if (this.changedFeatures.length) {
        this.showDialog = true
      } else {
        this.$refs['form'].save()
      }
    },
    afterSave(model) {
      setTimeout(() => {
        if (this.isCreatePage) {
          location.href = this.$router.resolve(
            projectRoute({ name: 'ProjectSettings' }, model.id)
          ).href
        } else {
          this.$router.go()
        }
      }, 1000)
    },
    confirmDelete() {
      this.$msgbox({
        title: this.$t('ProjectSettings_DialogDelete_Title'),
        message: this.$t('ProjectSettings_DialogDelete_Prompt'),
        showCancelButton: true,
        confirmButtonText: this.$t('ProjectSettings_Button_DeleteProject'),
        cancelButtonText: this.$t('ProjectSettings_Button_Cancel'),
        center: true
      }).then(() => {
        this.loading = true
        RestApi(this.deleteEndpoint)
          .then(() => {
            this.$message({
              message: this.$t('ProjectSettings_Message_DeleteSuccess'),
              type: 'success'
            })

            this.loading = false
            this.afterSave()
          })
          .catch(error => {
            this.$message({
              message: error.message,
              type: 'error'
            })
            this.loading = false
          })
      })
    },
    cancelDelete() {
      this.$msgbox({
        title: this.$t('ProjectSettings_DialogCancelDelete_Title'),
        message: this.$t('ProjectSettings_DialogCancelDelete_Prompt'),
        showCancelButton: true,
        confirmButtonText: this.$t('ProjectSettings_Button_CancelConfirm'),
        cancelButtonText: this.$t('ProjectSettings_Button_Cancel'),
        center: true
      }).then(() => {
        this.model.project_status = ProjectStatusEnum.ACTIVE
        this.loading = true
        this.cancelDeleteSave()
        this.loading = false
      })
    }
  }
}
</script>

<style scoped>
.cancel-delete-button {
  background-color: #fdc80d;
  color: #4b515f;
}

.cancel-delete-button:hover {
  opacity: 0.6;
  border-color: #fdc80d;
  background-color: #fdc80d;
}
</style>



// WEBPACK FOOTER //
// src/modules/project/pages/ProjectSettingsPage.vue